import { useState } from 'react';
import { useProject, useUpdateProject } from '../../hooks/useProjects';
import { Button, EmptyState, Input } from '../../ui';
import './RedirectURIsSettings.css';

const MAX_REDIRECT_URIS = 20;

interface RedirectURIsSettingsProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
}

export function RedirectURIsSettings({
  projectId,
  userRole = 'member',
}: RedirectURIsSettingsProps) {
  const { data: project, isLoading, error } = useProject(projectId);
  const updateProject = useUpdateProject();

  const [input, setInput] = useState('');
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const canManage = userRole === 'admin' || userRole === 'owner';
  const uris = project?.redirect_uris ?? [];
  const strictMode = project?.strict_redirect_uri_enforcement ?? false;
  const atCapacity = uris.length >= MAX_REDIRECT_URIS;

  const showFlash = (kind: 'success' | 'error', text: string) => {
    setFlash({ kind, text });
    window.setTimeout(() => setFlash(null), 4000);
  };

  const submitUpdate = async (data: {
    redirect_uris?: string[];
    strict_redirect_uri_enforcement?: boolean;
  }) => {
    try {
      await updateProject.mutateAsync({ projectId, data });
      return true;
    } catch (err) {
      showFlash('error', (err as Error).message);
      return false;
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    if (uris.includes(trimmed)) {
      showFlash('error', 'That redirect URI is already in the list.');
      return;
    }
    const ok = await submitUpdate({ redirect_uris: [...uris, trimmed] });
    if (ok) {
      setInput('');
      showFlash('success', 'Redirect URI added.');
    }
  };

  const handleRemove = async (uri: string) => {
    const ok = await submitUpdate({ redirect_uris: uris.filter(u => u !== uri) });
    if (ok) showFlash('success', 'Redirect URI removed.');
  };

  const handleToggleStrict = async (next: boolean) => {
    const ok = await submitUpdate({ strict_redirect_uri_enforcement: next });
    if (ok) {
      showFlash(
        'success',
        next ? 'Strict callback URL matching enabled.' : 'Strict callback URL matching disabled.'
      );
    }
  };

  if (isLoading) return <div className="redirects-settings__loading">Loading redirect URIs…</div>;
  if (error || !project) {
    return (
      <div className="redirects-settings__error">
        Failed to load project: {(error as Error | null)?.message ?? 'not found'}
      </div>
    );
  }

  return (
    <div className="redirects-settings">
      <div className="redirects-settings__header">
        <h2>Redirect URIs</h2>
        <p className="redirects-settings__muted">
          Exact callback URLs your apps may use after OAuth login. Path-level enforcement defends
          against same-origin open-redirect attacks (RFC 9700). Localhost entries match any port at
          runtime — register <code>http://localhost/auth/callback</code> once.
        </p>
      </div>

      {canManage && (
        <label className="redirects-settings__toggle">
          <input
            type="checkbox"
            checked={strictMode}
            disabled={updateProject.isPending}
            onChange={e => handleToggleStrict(e.target.checked)}
            aria-describedby="strict-mode-help"
          />
          <span className="redirects-settings__toggle-text">
            <strong>Require exact callback URL match (recommended)</strong>
            <span id="strict-mode-help" className="redirects-settings__toggle-help">
              When enabled, only URIs registered below are accepted. When disabled, any path on an
              allowed origin or verified domain is accepted.
            </span>
          </span>
        </label>
      )}

      {strictMode && uris.length === 0 && (
        <div className="redirects-settings__warning" role="alert">
          <strong>Strict mode is on but no redirect URIs are registered.</strong> All login flows
          that pass a <code>redirect_uri</code> will be rejected until you add at least one.
        </div>
      )}

      {flash && (
        <div className={`redirects-settings__flash redirects-settings__flash--${flash.kind}`}>
          {flash.text}
        </div>
      )}

      {canManage && (
        <form className="redirects-settings__add" onSubmit={handleAdd}>
          <label htmlFor="redirect-uri-input" className="visually-hidden">
            Redirect URI
          </label>
          <Input
            id="redirect-uri-input"
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="https://app.example.com/auth/callback"
            autoComplete="off"
            disabled={atCapacity || updateProject.isPending}
            wrapperClassName="redirects-settings__input"
          />
          <Button
            type="submit"
            disabled={!input.trim() || atCapacity || updateProject.isPending}
            loading={updateProject.isPending}
          >
            {updateProject.isPending ? 'Adding…' : 'Add URI'}
          </Button>
        </form>
      )}

      <p className="redirects-settings__meta">
        {uris.length} of {MAX_REDIRECT_URIS} used
      </p>

      {uris.length === 0 ? (
        <EmptyState
          title="No redirect URIs registered yet."
          description="Register the exact callback URLs your apps use after OAuth login."
        />
      ) : (
        <ul className="redirects-settings__list">
          {uris.map(uri => (
            <li key={uri} className="redirect-row">
              <code className="redirect-row__value">{uri}</code>
              {canManage && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemove(uri)}
                  disabled={updateProject.isPending}
                  aria-label={`Remove ${uri}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
